import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import ProjectCard from './components/ProjectCard';
import ServiceRequestCard from './components/ServiceRequestCard';
import ActivityCard from './components/ActivityCard';
import CommunicationPanel from './components/CommunicationPanel';
import ServiceRequestModal from './components/ServiceRequestModal';
import RequestHistoryTable from './components/RequestHistoryTable';

const ClientPortal = () => {
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [notifications, setNotifications] = useState(3);
  const navigate = useNavigate();

  // Mock data for projects
  const projects = [
    {
      id: 1,
      name: "ConsultFlow Pro",
      description: "Sistema integrado de gestão de consultorias com módulos de projetos, ordens de serviço e portal do cliente",
      status: "Em Andamento",
      progress: 75,
      startDate: "15/08/2024",
      endDate: "15/12/2024",
      teamSize: 4,
      activeOrders: 3
    },
    {
      id: 2,
      name: "Sistema de Gestão Integrada",
      description: "Implementação de ERP customizado para gestão financeira e operacional",
      status: "Em Andamento",
      progress: 45,
      startDate: "01/09/2024",
      endDate: "28/02/2025",
      teamSize: 6,
      activeOrders: 5
    },
    {
      id: 3,
      name: "Infraestrutura de TI",
      description: "Modernização da infraestrutura tecnológica com migração para nuvem",
      status: "Pausado",
      progress: 30,
      startDate: "10/07/2024",
      endDate: "10/01/2025",
      teamSize: 3,
      activeOrders: 1
    }
  ];

  // Mock data for service requests
  const serviceRequests = [
    {
      id: "2024-015",
      title: "Problema de conectividade na rede",
      category: "Técnico",
      priority: "Alta",
      status: "Em Andamento",
      description: "Intermitência na conexão de rede está afetando a produtividade da equipe. Problema iniciou ontem pela manhã.",
      createdDate: "30/10/2024",
      assignedTo: "João Silva",
      lastUpdate: "há 2 horas"
    },
    {
      id: "2024-014",
      title: "Solicitação de novo usuário",
      category: "Suporte",
      priority: "Média",
      status: "Aguardando Cliente",
      description: "Criação de acesso para novo colaborador no sistema de gestão.",
      createdDate: "29/10/2024",
      assignedTo: "Maria Santos",
      lastUpdate: "ontem"
    },
    {
      id: "2024-013",
      title: "Treinamento nova funcionalidade",
      category: "Treinamento",
      priority: "Baixa",
      status: "Aberta",
      description: "Capacitação da equipe para utilização da nova funcionalidade de relatórios.",
      createdDate: "28/10/2024",
      assignedTo: null,
      lastUpdate: "há 3 dias"
    }
  ];

  // Mock data for upcoming activities
  const upcomingActivities = [
    {
      id: 1,
      title: "Reunião de acompanhamento mensal",
      description: "Revisão do progresso dos projetos e definição de próximos passos",
      type: "Reunião",
      date: "05/11/2024",
      time: "14:00",
      technician: "Maria Santos"
    },
    {
      id: 2,
      title: "Manutenção preventiva servidor",
      description: "Manutenção programada do servidor principal - possível indisponibilidade",
      type: "Manutenção",
      date: "07/11/2024",
      time: "22:00",
      technician: "Carlos Oliveira"
    },
    {
      id: 3,
      title: "Instalação de equipamentos",
      description: "Instalação de novos servidores na sala de TI",
      type: "Instalação",
      date: "10/11/2024",
      time: "09:00",
      technician: "João Silva"
    }
  ];

  const handleNewRequest = () => {
    setIsRequestModalOpen(true);
  };

  const handleSubmitRequest = (requestData) => {
    console.log('Nova solicitação:', requestData);
    // Here you would typically send the request to your backend
    setNotifications(prev => prev + 1);
  };

  const handleViewProjectDetails = (project) => {
    navigate('/project-management', { state: { projectId: project?.id } });
  };

  const handleViewRequestDetails = (request) => {
    console.log('Ver detalhes da solicitação:', request);
  };

  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard' },
    { key: 'requests', label: 'Minhas Solicitações', icon: 'ClipboardList' },
    { key: 'history', label: 'Histórico', icon: 'History' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header onToggleSidebar={() => {}} />
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Portal do Cliente
            </h1>
            <p className="text-muted-foreground">
              Acompanhe seus projetos, solicite serviços e comunique-se com nossa equipe
            </p>
          </div>
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <Button
              variant="outline"
              iconName="Search"
              onClick={() => console.log('Buscar')}
            >
              Buscar
            </Button>
            <Button
              onClick={handleNewRequest}
              iconName="Plus"
              iconPosition="left"
            >
              Nova Solicitação
            </Button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 mb-6 border-b border-border">
          {tabs?.map((tab) => (
            <button
              key={tab?.key}
              onClick={() => setActiveTab(tab?.key)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-t-lg transition-colors ${
                activeTab === tab?.key
                  ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span className="font-medium">{tab?.label}</span>
              {tab?.key === 'requests' && notifications > 0 && (
                <span className="w-5 h-5 bg-error text-error-foreground text-xs rounded-full flex items-center justify-center">
                  {notifications}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Projetos Ativos</p>
                    <p className="text-2xl font-bold text-card-foreground">3</p>
                  </div>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="FolderOpen" size={24} className="text-primary" />
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Solicitações Abertas</p>
                    <p className="text-2xl font-bold text-card-foreground">5</p>
                  </div>
                  <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon name="ClipboardList" size={24} className="text-warning" />
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Técnicos Ativos</p>
                    <p className="text-2xl font-bold text-card-foreground">8</p>
                  </div>
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="Users" size={24} className="text-success" />
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Progresso Médio</p>
                    <p className="text-2xl font-bold text-card-foreground">62%</p>
                  </div>
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="TrendingUp" size={24} className="text-accent" />
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Projects and Activities */}
              <div className="lg:col-span-2 space-y-8">
                {/* Active Projects */}
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-foreground">Projetos Ativos</h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/project-management')}
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      Ver Todos
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {projects?.map((project) => (
                      <ProjectCard
                        key={project?.id}
                        project={project}
                        onViewDetails={handleViewProjectDetails}
                      />
                    ))}
                  </div>
                </div>

                {/* Upcoming Activities */}
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-foreground">Próximas Atividades</h2>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Calendar"
                      iconPosition="right"
                    >
                      Ver Agenda
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {upcomingActivities?.map((activity) => (
                      <ActivityCard key={activity?.id} activity={activity} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Communication */}
              <div className="space-y-8">
                <CommunicationPanel />
              </div>
            </div>
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Minhas Solicitações</h2>
              <Button
                onClick={handleNewRequest}
                iconName="Plus"
                iconPosition="left"
              >
                Nova Solicitação
              </Button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {serviceRequests?.map((request) => (
                <ServiceRequestCard
                  key={request?.id}
                  request={request}
                  onViewDetails={handleViewRequestDetails}
                />
              ))}
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            <RequestHistoryTable />
          </div>
        )}
      </div>
      {/* Service Request Modal */}
      <ServiceRequestModal
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        onSubmit={handleSubmitRequest}
      />
    </div>
  );
};

export default ClientPortal;